import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ticketService,
  SupportNotification,
} from '../../services/ticketService';
import { SUPPORT_NOTIFICATION_REFRESH_EVENT } from './supportNotificationEvents';

import './SupportNotificationBell.css';

const POLL_INTERVAL_MS = 30000;
const NOTIFICATION_PREVIEW_WORDS = 7;

const BellIcon: React.FC = () => (
  <svg
    className="support-notification-bell__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const formatTimeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);

  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;

  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(dateStr).toLocaleDateString();
};

const getInternalUrl = (url: string): string => {
  if (!url) return '/support';

  try {
    const parsed = new URL(url, window.location.origin);

    if (parsed.origin !== window.location.origin) return '/support';

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url.startsWith('/') ? url : '/support';
  }
};

const truncateWords = (text: string, maxWords: number): string => {
  const words = text.trim().split(/\s+/).filter(Boolean);

  if (words.length <= maxWords) return words.join(' ');

  return `${words.slice(0, maxWords).join(' ')}...`;
};

const getNotificationPreview = (notification: SupportNotification): string => {
  const preview = typeof notification.data?.preview === 'string'
    ? notification.data.preview
    : notification.body;

  return truncateWords(preview, NOTIFICATION_PREVIEW_WORDS);
};

export const SupportNotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<SupportNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const clearedNotificationIdsRef = useRef<Set<string>>(new Set());

  const refreshCount = useCallback(async () => {
    try {
      setUnreadCount(await ticketService.getUnreadNotificationCount());
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ticketService.listNotifications({
        page: 1,
        per_page: 20,
        unread_only: true,
      });

      setNotifications(data.notifications.filter(
        (notification) => !clearedNotificationIdsRef.current.has(notification.id)
      ));
      await refreshCount();
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();

    const interval = window.setInterval(refreshCount, POLL_INTERVAL_MS);
    const handleRefresh = () => {
      refreshCount();
      if (isOpen) loadNotifications();
    };

    window.addEventListener(SUPPORT_NOTIFICATION_REFRESH_EVENT, handleRefresh);
    window.addEventListener('focus', refreshCount);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener(SUPPORT_NOTIFICATION_REFRESH_EVENT, handleRefresh);
      window.removeEventListener('focus', refreshCount);
    };
  }, [isOpen, loadNotifications, refreshCount]);

  useEffect(() => {
    if (isOpen) loadNotifications();
  }, [isOpen, loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: SupportNotification) => {
    if (!notification.read_at) {
      await ticketService.markNotificationRead(notification.id).catch(() => {});
      setUnreadCount((count) => Math.max(0, count - 1));
    }

    setIsOpen(false);
    navigate(getInternalUrl(notification.url));
  };

  const handleClearAll = async () => {
    notifications.forEach((notification) => {
      clearedNotificationIdsRef.current.add(notification.id);
    });
    await ticketService.markAllNotificationsRead().catch(() => {});
    setUnreadCount(0);
    setNotifications([]);
  };

  return (
    <div className="support-notification-bell" ref={rootRef}>
      <button
        type="button"
        className={`support-notification-bell__button${isOpen ? ' support-notification-bell__button--open' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="support-notification-bell__badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="support-notification-bell__dropdown">
          <div className="support-notification-bell__header">
            <span className="support-notification-bell__title">Notifications</span>
            <button
              type="button"
              className="support-notification-bell__read-all"
              onClick={handleClearAll}
              disabled={notifications.length === 0}
            >
              Clear all
            </button>
          </div>

          <div className="support-notification-bell__list">
            {loading ? (
              <div className="support-notification-bell__state">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="support-notification-bell__state">No notifications</div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className={`support-notification-bell__item${notification.read_at ? '' : ' support-notification-bell__item--unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <span className="support-notification-bell__dot" />
                  <span>
                    <span className="support-notification-bell__item-title">
                      {notification.title}
                    </span>
                    <span className="support-notification-bell__item-body">
                      {getNotificationPreview(notification)}
                    </span>
                    <span className="support-notification-bell__item-time">
                      {formatTimeAgo(notification.created_at)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
